import { service } from '@ember/service';

import type Highcharts from 'highcharts';

import type { DChartInterface, Service } from 'webapp-types';
import { CompareType, DataSelectType } from 'webapp/enums/enums';
import { copyObject } from 'webapp/utils/common';

import LineOrBarChartRenderer from 'webapp/d-chart/renderer/line-or-bar-chart';

// dataPoint isn't set on the chart points anymore (since #7461), so the inverted check below is always false
type GoalChartDataPoint = DChartInterface.ChartDataPoint & {
	dataPoint?: { changeInvert?: boolean };
};

export default class GoalChartRenderer extends LineOrBarChartRenderer {
	@service('model-manager/goals')
	declare mmGoalsService: Service.ModelManager.Goals;

	override get dataOptions(): Highcharts.Options {
		const options = super.dataOptions,
			mainDataSelect = this.visualization.block.dataSelects.find(
				(item) => item.type === DataSelectType.MAIN,
			);

		let goalStatus;

		options.series!.forEach((s) => {
			(s as Highcharts.SeriesLineOptions).connectNulls = false;
		});

		if (mainDataSelect) {
			const secondaryDataSelects =
					this.visualization.blockSetup.getSecondaryDataSelectsForDataSelect(
						mainDataSelect,
					),
				numberDataSelect = secondaryDataSelects.find(
					(item) => item.type === DataSelectType.MAIN,
				),
				output = numberDataSelect?.dataRoot?.outputs[0],
				goalComparePoint = output?.point?.comparePoints.find(
					(cp) => cp.compareSettings?.type === CompareType.GOAL,
				);
			goalStatus = goalComparePoint?.extra?.goalStatus;
		}

		const primaryPointSeries = options.series!.find(
			(series) =>
				(series as DChartInterface.SeriesOptions).dbxSeries!.type === DataSelectType.MAIN,
		) as Highcharts.SeriesLineOptions | undefined;
		const primaryPoints = primaryPointSeries?.data as GoalChartDataPoint[] | undefined;

		if (primaryPoints) {
			const isInverted = !!primaryPoints[0]?.dataPoint?.changeInvert,
				goalStatusStr = this.mmGoalsService.getStatusClass(goalStatus);

			const className = GoalChartRenderer.getClassName(goalStatusStr, isInverted);

			primaryPointSeries!.className = className; // primaryPoints come from it
		}

		return options;
	}

	override get themeOptions(): Highcharts.Options {
		let options = super.themeOptions;

		const defs: Record<string, DChartInterface.Def> = {};

		GoalChartRenderer.getGradientDefs().forEach((def) => {
			defs[def.id] = def;
		});

		options = this.mergeOptions(options, { defs });

		return options;
	}

	static getClassName(progressClass: string, isInverted: boolean): string {
		return `zonetype-${isInverted ? 'inverted' : 'normal'}-${progressClass}`;
	}

	static getGradientDefs(): DChartInterface.Def[] {
		const defs: DChartInterface.Def[] = [];

		const commonProps: DChartInterface.DefNode & { children: DChartInterface.DefNode[] } = {
			tagName: 'linearGradient',
			x1: 0,
			y1: 0,
			x2: 0,
			y2: 0,
			children: [
				{
					tagName: 'stop',
					offset: '0%',
					//'stop-color': '',
					'stop-opacity': 0.5,
				},
				{
					tagName: 'stop',
					offset: '100%',
					//'stop-color': '',
					'stop-opacity': 0,
				},
			],
		};

		// green
		let color = '#02b875'; // @graph_green
		commonProps.children.forEach((c) => {
			c['stop-color'] = color;
		});

		let props = copyObject(commonProps) as DChartInterface.Def; // id is set right below
		props.id = 'gradient-goal-green-tb'; // tb = top to bottom
		props.y2 = 1;
		defs.push(props);

		// orange
		color = '#fa870c'; // --databox-orange-500
		commonProps.children.forEach((c) => {
			c['stop-color'] = color;
		});

		props = copyObject(commonProps) as DChartInterface.Def;
		props.id = 'gradient-goal-orange-tb'; // tb = top to bottom
		props.y2 = 1;
		defs.push(props);

		// red
		color = '#f25b4d';
		commonProps.children.forEach((c) => {
			c['stop-color'] = color;
		});

		props = copyObject(commonProps) as DChartInterface.Def;
		props.id = 'gradient-goal-red-tb'; // tb = top to bottom
		props.y2 = 1;
		defs.push(props);

		// gray
		color = '#8e929b'; // --databox-neutral-400
		commonProps.children.forEach((c) => {
			c['stop-color'] = color;
		});

		props = copyObject(commonProps) as DChartInterface.Def;
		props.id = 'gradient-goal-gray-tb'; // tb = top to bottom
		props.y2 = 1;
		defs.push(props);

		return defs;
	}
}
